import { ActivatedRouteSnapshot } from '@angular/router'
import { gt } from 'ramda'
import { CreditModuleContext } from '../model/credit-routes.model'
import {
  CreditStructure,
  CreditCalculationStructure,
  CreditTransaction,
  TRANSACTION_TEMP_PREFIX,
} from '../model/credit-structure.model'
import {
  AnimatedScenariosResults,
  AnimatedScenariosResultsData,
} from '../model/credit-animated-scenarios.model'
import { CreditMetricSetting } from '../model/credit-metric.model'
import { CreditCompareMetric, CreditStructureCompareEntity } from '../model/credit-compare.model'
import { CreditStructureGroup } from '../model/credit-structure-group.model'
import {
  CreditSubmissionStatus,
  CreditSubmissionStatusClass,
} from '../model/credit-submission.model'
import {
  CreditGrossLossScenario,
  CreditModelScenarioGlsMapping,
  CreditModelScenarioMapping,
  ScenarioCategory,
  ScenarioType,
  GROSS_LOSS_SCENARIO_MODEL_RUNNER_METADATA_KEYS,
} from '../model/credit-portfolio.model'

/**
 * Returns true if date1 is later than date2.
 * Returns false if date1 is earlier than or equal to date2.
 */
export const isDateLatest = (date1: string | Date, date2: string | Date): boolean =>
  gt(new Date(date1).getTime(), new Date(date2).getTime())

export const getDifferenceBetweenDates = (date1: Date, date2: Date): number =>
  date2.getFullYear() - date1.getFullYear()

/** current time in milliseconds */
export const getTodayTime = (): number => {
  return new Date().getTime()
}

/** `YYYY-MM-DD format` */
export const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0]
}

/** `YYYY-MM-DDTHH:mm:ss.SSS` format */
export const getTodayDateTime = (): string => {
  return new Date().toISOString().slice(0, -1)
}

/** Ensures a date string is formatted as `YYYY-MM-DD` */
export const formatDateToYYYYMMDD = (raw: string): string => {
  const d = new Date(raw)
  if (isNaN(d.getTime())) return raw
  return d.toISOString().split('T')[0]
}

/** `YYYY-MM-DDTHH:mm:ss.SSS` format w/ time `00:00:00.000` (UTC) */
export const getCleanDateTime = (date: string): string => {
  const cleanDate = new Date(date)
  cleanDate.setUTCHours(0, 0, 0, 0)
  return cleanDate.toISOString().slice(0, -1)
}

export const removeTempTransactionIds = (
  structure: CreditCalculationStructure,
): CreditCalculationStructure => {
  return {
    ...structure,
    transactions: structure.transactions.map(({ _id, ...rest }) => {
      return _id.includes(TRANSACTION_TEMP_PREFIX) ? (rest as CreditTransaction) : { _id, ...rest }
    }),
  }
}

export const isDefined = <T>(arg: T | null | undefined): arg is T => arg != null

export const isDefinedString = (prop?: string): prop is string =>
  isDefined(prop) && prop.trim() !== ''

export const isDefinedNumber = (prop?: number): prop is number => isDefined(prop) && !isNaN(prop)

export const convertAnimatedScenariosData = (
  AnimatedScenariosResultsData: AnimatedScenariosResultsData,
): AnimatedScenariosResults[] => {
  let results: AnimatedScenariosResults[] = []
  let id = 0
  if (AnimatedScenariosResultsData.data && AnimatedScenariosResultsData.data.length > 0) {
    AnimatedScenariosResultsData.data.map((as, i) => {
      id = id + i + 1
      results.push({ ...as, id, isActuals: false })
    })
  }
  if (
    AnimatedScenariosResultsData.actualsData &&
    AnimatedScenariosResultsData.actualsData.length > 0
  ) {
    AnimatedScenariosResultsData.actualsData.map((as, i) => {
      id = id + i + 1
      results.push({ ...as, id, isActuals: true })
    })
  }
  return results
}

/** returns label of format `label-scenario`*/
export const getMetricLabel = (label: string, scenario: string): string => {
  const newLabel = label.split('-')[0].trim()
  return `${newLabel} - ${scenario}`
}

export const getMetricLabelParts = (label: string): string[] => {
  return label.split('-').map((part) => part.trim())
}

export const getTotalWeight = (metricSettings: CreditMetricSetting[]): number => {
  return metricSettings.reduce((totalWeight, metricSetting) => {
    return metricSetting.show ? totalWeight + metricSetting.weight : totalWeight
  }, 0)
}

export const getCompareEntityForStructure = (
  creditStructure: CreditStructure,
  metrics: CreditCompareMetric[],
): CreditStructureCompareEntity => {
  return {
    credit_calculation_structure_id: creditStructure.credit_calculation_structure_id,
    name: creditStructure.label,
    thumbnails: [creditStructure.imageSrc ?? ''],
    metrics,
  }
}

export const getCompareEntityForStructureGroup = (
  creditStructureGroup: CreditStructureGroup,
  metrics: CreditCompareMetric[],
  thumbnails: string[],
): CreditStructureCompareEntity => {
  return {
    credit_calculation_structure_id: creditStructureGroup.credit_calculation_structure_id,
    name: creditStructureGroup.label,
    thumbnails,
    metrics,
  }
}

export const useActuals = (context: CreditModuleContext): boolean => {
  return context === CreditModuleContext.Monitoring
}

export const getParamFromRootUrl = (
  router: ActivatedRouteSnapshot,
  searchParam: string,
): string => {
  const foundParam = router.params[searchParam] as string | undefined
  if (foundParam) {
    // base case
    return foundParam
  }

  if (router.children.length > 0) {
    // recursive case
    return getParamFromRootUrl(router.children[0], searchParam)
  }
}

export const getStatusClass = (status: CreditSubmissionStatus): CreditSubmissionStatusClass => {
  if (status === CreditSubmissionStatus.Draft) {
    return CreditSubmissionStatusClass.Draft
  }
  if (status === CreditSubmissionStatus.Final) {
    return CreditSubmissionStatusClass.Final
  }
  if (status === CreditSubmissionStatus.Approved) {
    return CreditSubmissionStatusClass.Approved
  }
  if (status === CreditSubmissionStatus.Released) {
    return CreditSubmissionStatusClass.Released
  }
  if (status === CreditSubmissionStatus.Cancelled) {
    return CreditSubmissionStatusClass.Cancelled
  }
}

export const incrementYearOnDateString = (dateText: string, increment: number): string => {
  const date = new Date(dateText)
  const year = date.getFullYear()
  date.setFullYear(year + increment)
  return date.toISOString().split('T')[0]
}

export const isMappingIdMatching = (
  mappingId: number,
  gls: CreditGrossLossScenario,
  defaultStochastic?: boolean,
): boolean => {
  return gls.scenario_type === ScenarioType.Deterministic
    ? mappingId === gls.scenario_mapping_id
    : defaultStochastic
      ? mappingId === gls.stochastic_data.at(0)?.scenario_mapping_id
      : gls.stochastic_data.some((stochastic) => mappingId === stochastic.scenario_mapping_id)
}

/** sort ascending on `model_order`, `scenario_order` fields respectively */
export const sortModelScenarioMapping = (
  a: CreditModelScenarioMapping,
  b: CreditModelScenarioMapping,
): number => {
  return a.model_order - b.model_order || a.scenario_order - b.scenario_order
}

export const toScenarioCategoryOption = (
  category: ScenarioCategory,
  mapping: CreditModelScenarioMapping,
  scenarios: CreditGrossLossScenario[],
): CreditModelScenarioGlsMapping[] => {
  const gls = scenarios?.find((gls) => isMappingIdMatching(mapping.id, gls))
  return gls
    ? [
        {
          ...mapping,
          _id: gls._id,
          key: toScenarioCategoryOptionKey(category, mapping),
          category,
          text: toScenarioCategoryOptionText(category, mapping, gls),
        },
      ]
    : []
}

const toScenarioCategoryOptionKey = (
  category: ScenarioCategory,
  mapping: CreditModelScenarioMapping,
): string => {
  return [category, mapping.id].join('-')
}

const toScenarioCategoryOptionText = (
  category: ScenarioCategory,
  mapping: CreditModelScenarioMapping,
  gls: CreditGrossLossScenario,
): string => {
  const scenarioString = mapping.return_period
    ? `${gls.scenario} - Return Period ${mapping.return_period}`
    : gls.scenario
  const differentiator = category === ScenarioCategory.Origination ? '' : ' ' // ! needed since syncfusion cannot distinguish items with same text even with different values
  return `${gls.model} ${scenarioString}${differentiator}`
}

export const getScenarioFromMappingId = (
  mappingId: string,
  scenarios: CreditGrossLossScenario[],
): CreditGrossLossScenario => scenarios.find((gls) => isMappingIdMatching(Number(mappingId), gls))

export const getMappingFromScenarioId = (
  scenarioId: string,
  scenarios: CreditGrossLossScenario[],
  mappings: CreditModelScenarioMapping[],
): CreditModelScenarioMapping => {
  if (!scenarioId || !scenarios || !mappings) {
    return
  }
  const scenario = scenarios.find((gls) => gls._id === scenarioId)
  return mappings.find((mapping) => isMappingIdMatching(mapping.id, scenario))
}

export const getMappingsFromScenarios = (
  scenarios: CreditGrossLossScenario[],
  mappings: CreditModelScenarioMapping[],
): CreditModelScenarioMapping[] => {
  if (!scenarios || !mappings) {
    return []
  }
  return scenarios.map((gls) =>
    mappings.find((mapping) => isMappingIdMatching(mapping.id, gls, true)),
  )
}

/**
 * Checks if a scenario was imported from Model Runner
 * @param scenario The scenario to check
 * @returns true if the scenario has a runId in its metadata, false otherwise
 */
export const isImportedFromModelRunner = (scenario: CreditGrossLossScenario): boolean => {
  return !!scenario.metadata?.[GROSS_LOSS_SCENARIO_MODEL_RUNNER_METADATA_KEYS.RUN_ID]
}

/**
 * Gets the Model Runner metadata from a scenario
 * @param scenario The scenario to get the metadata from
 * @returns The Model Runner metadata
 */
export const getModelRunnerMetadata = (scenario: CreditGrossLossScenario) => {
  return {
    runId: scenario.metadata?.[GROSS_LOSS_SCENARIO_MODEL_RUNNER_METADATA_KEYS.RUN_ID],
    importDate: scenario.metadata?.[GROSS_LOSS_SCENARIO_MODEL_RUNNER_METADATA_KEYS.IMPORT_DATE],
  }
}
